import { Breadcrumbs, Button, SearchBar, Spinner } from '@platform/ui-kit'
import { useRouter } from 'next/router'
import { ReactElement, useCallback, useMemo, useRef } from 'react'
import {
  WebsiteLayout,
  ArticleList,
  CategoryBlogFrame,
  HEADER_THEMES,
  useUpdateHeader,
  useCleanHeader,
  WebsiteSeo,
} from '@platform/website-components'
import { formatWebsitePostCategory, websiteFormatArticleList } from '@platform/utils'
import { useForm } from 'react-hook-form'
import {
  useInfiniteGetAllBlogPostsQuery,
  SortingDirection,
  useGetAllBlogPostsQuery,
} from '@platform/data-access'
import { ChevronLeftIcon } from '@icons'
import * as Sentry from '@sentry/nextjs'
import { dehydrate, QueryClient } from '@tanstack/react-query'
import type { GetServerSideProps } from 'next'

import styles from './category.module.scss'

const FIRST_PAGE = 1
const PAGES_SIZE = 12
const LATEST_NEWS = 'latest-news'

const SEO = {
  title: 'Blog // Olby',
  description:
    'Insights on international career, remote work, productivity, work-life balance, engineering, team management and leadership.',
}

type FormData = {
  searchPostCategory: string
}

export const GuidesCategory = () => {
  const {
    register,
    reset,
    handleSubmit,
    formState: { dirtyFields },
  } = useForm<FormData>({ mode: 'onSubmit', defaultValues: { searchPostCategory: '' } })
  const router = useRouter()
  const contentRef = useRef<HTMLDivElement | null>(null)
  const category = useMemo(
    () => formatWebsitePostCategory(router.query.category),
    [router.query.category]
  )
  const isLatestNews = router.query.category === LATEST_NEWS

  const { data, fetchNextPage, isFetchingNextPage } = useInfiniteGetAllBlogPostsQuery(
    {
      input: {
        ...(isLatestNews
          ? { sort: [{ createdAt: SortingDirection.Desc }] }
          : {
              filters: {
                category: { name: { containsi: category } },
              },
            }),
        pagination: {
          page: FIRST_PAGE,
          pageSize: PAGES_SIZE,
        },
      },
    },
    {
      enabled: false,
      keepPreviousData: true,
      getNextPageParam: lastPage => {
        const lastPageNumber = Number(lastPage.getAllBlogPosts?.pagination?.page)

        if (
          lastPageNumber !== lastPage.getAllBlogPosts?.pagination?.pageCount &&
          !!lastPage.getAllBlogPosts?.pagination?.pageCount
        ) {
          return {
            input: {
              ...(isLatestNews
                ? { sort: [{ createdAt: SortingDirection.Desc }] }
                : {
                    filters: {
                      category: { name: { containsi: category } },
                    },
                  }),
              pagination: {
                page: lastPageNumber + 1,
                pageSize: PAGES_SIZE,
              },
            },
          }
        }
        return undefined
      },
    }
  )

  const articleList = useMemo(() => {
    if (!data?.pages) {
      return []
    }

    const list = data.pages.flatMap(page => page.getAllBlogPosts.data)

    return websiteFormatArticleList(list)
  }, [data])

  const crumbs = useMemo(
    () => [
      { id: 'blog', label: 'Blog', href: '/blog' },
      { id: `${category}`, label: `${category}`, href: '/' },
    ],
    [category]
  )

  const onSearchClear = useCallback(() => {
    reset({ searchPostCategory: '' })
  }, [reset])

  const onSubmit = handleSubmit(data => {
    if (dirtyFields.searchPostCategory) {
      router.push({
        pathname: '/blog/search-results',
        query: { category: router.query.category, searchPostsCategory: data.searchPostCategory },
      })
    }
  })

  const onLoadMore = useCallback(() => {
    fetchNextPage()
  }, [fetchNextPage])

  useUpdateHeader(contentRef, HEADER_THEMES.NEUTRAL_BACKGROUND)
  useCleanHeader()

  return (
    <div className={styles['main-search-content']} ref={contentRef}>
      <WebsiteSeo seo={SEO} />
      <header className={styles['guides-category-header']}>
        <Breadcrumbs crumbs={crumbs} className={styles['breadcrumbs']} />
        {/* TODO: Remove this button once breadcrumbs updated for mobile  */}
        <Button
          startAdornment={ChevronLeftIcon}
          className={styles['back-mobile-button']}
          href="/blog"
          prefetch={false}
        >
          Blog
        </Button>
        <div className={styles['category-title-container']}>
          <CategoryBlogFrame className={styles['frame']} />
          <h1 className={styles['page-category-title']}>{category}</h1>
        </div>
        <form className={styles['search-wrapper']} onSubmit={onSubmit}>
          <SearchBar
            id="search-post"
            label={`Search ${category} guides`}
            hiddenLabel
            variant="search-with-submit"
            placeholder={`Search for ${category.toLowerCase()} posts`}
            onSearchClear={onSearchClear}
            {...register('searchPostCategory')}
          />
        </form>
      </header>
      <div className={styles['articles-wrapper']}>
        {Boolean(articleList.length) && (
          <ArticleList
            posts={articleList}
            onLoadMore={onLoadMore}
            hideCategoryTag={!isLatestNews}
            className={styles['articles-list']}
          />
        )}
        {isFetchingNextPage && (
          <div className={styles['spinner-container']}>
            <Spinner size="large" />
          </div>
        )}
      </div>
    </div>
  )
}

export const getServerSideProps: GetServerSideProps = async context => {
  try {
    const queryClient = new QueryClient()
    const isLatestNews = context.query.category === LATEST_NEWS
    const category = formatWebsitePostCategory(context.query.category)

    await queryClient.prefetchInfiniteQuery(
      [
        'GetAllBlogPosts.infinite',
        {
          input: {
            ...(isLatestNews
              ? { sort: [{ createdAt: SortingDirection.Desc }] }
              : {
                  filters: {
                    category: { name: { containsi: category } },
                  },
                }),
            pagination: {
              page: FIRST_PAGE,
              pageSize: PAGES_SIZE,
            },
          },
        },
      ],
      useGetAllBlogPostsQuery.fetcher({
        input: {
          ...(isLatestNews
            ? { sort: [{ createdAt: SortingDirection.Desc }] }
            : {
                filters: {
                  category: { name: { containsi: category } },
                },
              }),
          pagination: {
            page: FIRST_PAGE,
            pageSize: PAGES_SIZE,
          },
        },
      })
    )

    // Parse and stringify based on:
    // https://github.com/TanStack/query/issues/1458#issuecomment-747716357
    return {
      props: {
        dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
      },
    }
  } catch (error) {
    const errorMessage = error as { message: string }
    Sentry.captureMessage(errorMessage.message)
    console.error(error)
    return {
      notFound: true,
    }
  }
}

export default GuidesCategory

GuidesCategory.getLayout = (page: ReactElement) => {
  return (
    <WebsiteLayout shouldChangeBgColor headerInitialTheme={HEADER_THEMES.NEUTRAL_BACKGROUND}>
      {page}
    </WebsiteLayout>
  )
}
